import {
    Autocomplete,
    CardActionArea,
    Grid2,
    MenuItem,
    Pagination,
    Select,
    Stack,
    TextField,
    Typography,
    Tooltip,
    useMediaQuery,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button
} from "@mui/material";
import axios from "axios";
import {useMemo, useState} from "react";
import {useSource} from "avrora";

interface AuditEvent {
    email: string;
    timestamp: string;
    traceId: string;
    action: string;
    service: string;
    message: string;
}

interface AuditLogFilter {
    action?: string[];
    email?: string;
    timestampFrom?: string;
    timestampTo?: string;
    traceId?: string;
    service?: string[];
    message?: string;
}

const AUDIT_ACTIONS = [
    "USER_REGISTERED", "USER_ACTIVATED", "USER_BLOCKED", "USER_SUSPENDED",
    "USER_RESET_PASSWORD", "USER_INVITED", "SETTINGS_CHANGED", "USER_LOGIN",
    "INCORRECT_PASSWORD", "EMAIL_SENT", "DECRYPTION_REQUESTED",
    "MAX_FAILED_LOGINS", "WRONG_LOGIN_ATTEMPT", "RESET_2_FA_SECRET",
    "USER_CONFIRMED_BY_OPERATOR", "ROLE_CHANGED", "OTHER"
];

const AUDIT_SERVICES = ["ECFG", "ES", "EOS", "EUS"];

export const Audit = () => {
    const isMobile = useMediaQuery("(max-width:499px)");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [emailInput, setEmailInput] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [actions, setActions] = useState<string[]>([]);
    const [timestampFromInput, setTimestampFromInput] = useState<string>("");
    const [timestampFrom, setTimestampFrom] = useState<string>("");
    const [timestampToInput, setTimestampToInput] = useState<string>("");
    const [timestampTo, setTimestampTo] = useState<string>("");
    const [traceIdInput, setTraceIdInput] = useState<string>("");
    const [traceId, setTraceId] = useState<string>("");
    const [services, setServices] = useState<string[]>([]);
    const [messageInput, setMessageInput] = useState<string>("");
    const [message, setMessage] = useState<string>("");
    const [totalPages, setTotalPages] = useState(1);
    const [totalRows, setTotalRows] = useState(0);
    const [eventModalOpen, setEventModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<AuditEvent | null>(null);

    const formatDateForAPI = (dateValue: string, isEnd = false): string => {
        if (!dateValue) return "";
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return "";
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        if (isEnd) {
            return `${year}-${month}-${day}T23:59:59`;
        }
        return `${year}-${month}-${day}T00:00:00`;
    };

    const filter: AuditLogFilter = useMemo(() => {
        const f: AuditLogFilter = {};
        if (actions.length > 0) f.action = actions;
        if (email) f.email = email;
        if (timestampFrom) {
            const formatted = formatDateForAPI(timestampFrom, false);
            if (formatted) f.timestampFrom = formatted;
        }
        if (timestampTo) {
            const formatted = formatDateForAPI(timestampTo, true);
            if (formatted) f.timestampTo = formatted;
        }
        if (traceId) f.traceId = traceId;
        if (services.length > 0) f.service = services;
        if (message) f.message = message;
        return f;
    }, [actions, email, timestampFrom, timestampTo, traceId, services, message]);

    const filterString = useMemo(() => JSON.stringify(filter), [filter]);

    const {data: events} = useSource<AuditEvent[]>(() => {
        return axios.post(`/ecfg/audit/list?page=${page}&size=${pageSize}`, filter, {
            headers: {
                'Accept': 'application/json'
            }
        }).then(resp => {
            const totalPagesHeader = resp.headers['x-list-total-pages'] || resp.headers['X-List-Total-Pages'];
            const totalRowsHeader = resp.headers['x-list-total-rows'] || resp.headers['X-List-Total-Rows'];
            if (totalPagesHeader) {
                setTotalPages(parseInt(String(totalPagesHeader)));
            } else if (totalRowsHeader) {
                const rows = parseInt(String(totalRowsHeader));
                setTotalRows(rows);
                setTotalPages(Math.ceil(rows / pageSize));
            }
            return resp.data;
        });
    }, [page, pageSize, filterString]);

    const formatDate = (dateString: string) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleString("ru-RU");
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
        }).catch(() => {});
    };

    const getFieldLabel = (key: string): string => {
        const labels: Record<string, string> = {
            email: "Email",
            timestamp: "Время",
            traceId: "Trace ID",
            action: "Действие",
            service: "Сервис",
            message: "Сообщение"
        };
        return labels[key] || key;
    };

    const formatFieldValue = (key: string, value: string): string => {
        if (key === "timestamp") {
            return formatDate(value);
        }
        return value || "";
    };

    return (
        <Stack
            maxWidth={isMobile ? "100%" : "1024px"}
            minWidth={isMobile ? "0" : "1024px"}
            padding={isMobile ? "1rem" : "3rem"}
            spacing={2}
            sx={{ width: "100%", boxSizing: "border-box", overflowX: "hidden" }}
        >
            <Typography variant="h1" fontSize={isMobile ? "1.5rem" : "inherit"}>
                Аудит
            </Typography>

            <Stack spacing={2} sx={{ width: "100%" }}>
                <Stack 
                    direction={isMobile ? "column" : "row"} 
                    spacing={2} 
                    flexWrap="wrap"
                    sx={{ width: "100%" }}
                >
                    <TextField
                        size="small"
                        label="Email"
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        onBlur={() => setEmail(emailInput)}
                        sx={{ minWidth: isMobile ? "100%" : 200, maxWidth: isMobile ? "100%" : 200 }}
                    />

                    <TextField
                        type="date"
                        size="small"
                        label="От"
                        value={timestampFromInput}
                        onChange={(e) => setTimestampFromInput(e.target.value)}
                        onBlur={() => setTimestampFrom(timestampFromInput)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: isMobile ? "100%" : 200, maxWidth: isMobile ? "100%" : 200 }}
                    />

                    <TextField
                        type="date"
                        size="small"
                        label="До"
                        value={timestampToInput}
                        onChange={(e) => setTimestampToInput(e.target.value)}
                        onBlur={() => setTimestampTo(timestampToInput)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: isMobile ? "100%" : 200, maxWidth: isMobile ? "100%" : 200 }}
                    />

                    <Autocomplete
                        multiple
                        options={AUDIT_ACTIONS}
                        value={actions}
                        onChange={(_, value: string[]) => setActions(value)}
                        getOptionLabel={(option) => option}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                label="Тип действия"
                            />
                        )}
                        sx={{ minWidth: isMobile ? "100%" : 200, maxWidth: isMobile ? "100%" : 200 }}
                    />
                </Stack>

                <Stack 
                    direction={isMobile ? "column" : "row"} 
                    spacing={2} 
                    flexWrap="wrap"
                    sx={{ width: "100%" }}
                >
                    <Autocomplete
                        multiple
                        options={AUDIT_SERVICES}
                        value={services}
                        onChange={(_, value: string[]) => setServices(value)}
                        getOptionLabel={(option) => option}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                label="Сервис"
                            />
                        )}
                        sx={{ minWidth: isMobile ? "100%" : 200, maxWidth: isMobile ? "100%" : 200 }}
                    />

                    <TextField
                        size="small"
                        label="Trace ID"
                        value={traceIdInput}
                        onChange={(e) => setTraceIdInput(e.target.value)}
                        onBlur={() => setTraceId(traceIdInput)}
                        sx={{ minWidth: isMobile ? "100%" : 200, maxWidth: isMobile ? "100%" : 200 }}
                    />

                    <TextField
                        size="small"
                        label="Сообщение"
                        value={messageInput}
                        onChange={(e) => setMessageInput(e.target.value)}
                        onBlur={() => setMessage(messageInput)}
                        sx={{ minWidth: isMobile ? "100%" : 200, maxWidth: isMobile ? "100%" : 200 }}
                    />
                </Stack>
            </Stack>

            <Stack>
                <Stack borderRadius={6} p={1} pl={2}>
                    <Grid2 container spacing={1} alignItems="center">
                        <Grid2 size={{xs: 12, sm: 2}}>
                            <Typography variant="body2">Trace ID</Typography>
                        </Grid2>
                        <Grid2 size={{xs: 12, sm: 2}}>
                            <Typography variant="body2">Email</Typography>
                        </Grid2>
                        <Grid2 size={{xs: 12, sm: 1.5}}>
                            <Typography variant="body2">Действие</Typography>
                        </Grid2>
                        <Grid2 size={{xs: 12, sm: 1.5}}>
                            <Typography variant="body2">Сервис</Typography>
                        </Grid2>
                        <Grid2 size={{xs: 12, sm: 2}}>
                            <Typography variant="body2">Время</Typography>
                        </Grid2>
                        <Grid2 size={{xs: 12, sm: 3}}>
                            <Typography variant="body2">Сообщение</Typography>
                        </Grid2>
                    </Grid2>
                </Stack>

                {events?.map((event, index) => (
                    <Stack key={`${event.traceId}-${index}`} borderRadius={6} mt={0.5}>
                        <CardActionArea
                            onClick={() => {
                                setSelectedEvent(event);
                                setEventModalOpen(true);
                            }}
                        >
                            <Stack p={1} pl={2}>
                                <Grid2 container spacing={1} alignItems="center">
                                    <Grid2 size={{xs: 12, sm: 2}}>
                                        <Tooltip title={event.traceId} arrow>
                                            <Typography 
                                                style={{ cursor: "pointer" }} 
                                                noWrap
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    copyToClipboard(event.traceId);
                                                }}
                                            >
                                                {event.traceId.substring(0, 8)}...
                                            </Typography>
                                        </Tooltip>
                                    </Grid2>
                                    <Grid2 size={{xs: 12, sm: 2}}>
                                        <Typography noWrap style={{ cursor: "pointer" }}>{event.email}</Typography>
                                    </Grid2>
                                    <Grid2 size={{xs: 12, sm: 1.5}}>
                                        <Typography noWrap style={{ cursor: "pointer" }}>{event.action}</Typography>
                                    </Grid2>
                                    <Grid2 size={{xs: 12, sm: 1.5}}>
                                        <Typography noWrap style={{ cursor: "pointer" }}>{event.service}</Typography>
                                    </Grid2>
                                    <Grid2 size={{xs: 12, sm: 2}}>
                                        <Typography style={{ cursor: "pointer" }}>{formatDate(event.timestamp)}</Typography>
                                    </Grid2>
                                    <Grid2 size={{xs: 12, sm: 3}}>
                                        <Typography 
                                            noWrap
                                            style={{ cursor: "pointer" }}
                                        >
                                            {event.message}
                                        </Typography>
                                    </Grid2>
                                </Grid2>
                            </Stack>
                        </CardActionArea>
                    </Stack>
                ))}
            </Stack>

            <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} mt={2}>
                <Select
                    size="small"
                    value={pageSize}
                    onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(1);
                    }}
                    sx={{ minWidth: 120 }}
                >
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={40}>40</MenuItem>
                    <MenuItem value={60}>60</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                </Select>
                <Pagination
                    page={page}
                    count={totalPages}
                    onChange={(_, p) => setPage(p)}
                    size="small"
                />
            </Stack>

            <Dialog
                open={eventModalOpen}
                onClose={() => setEventModalOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Детали события</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {selectedEvent && Object.entries(selectedEvent).map(([key, value]) => (
                            <Stack key={key} spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                    {getFieldLabel(key)}
                                </Typography>
                                <Typography sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                    {formatFieldValue(key, value)}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEventModalOpen(false)}>Закрыть</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

